var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { DateTimeRange, KeyDownProvider, useParcaContext } from '@parca/components';
import { store } from '@parca/store';
import { ProfileSelectionFromParams, SuffixParams } from '..';
import { useProfileTypes } from '../ProfileSelector';
import ProfileExplorerCompare from './ProfileExplorerCompare';
import ProfileExplorerSingle from './ProfileExplorerSingle';
var getExpressionAsAString = function (expression) {
    var x = Array.isArray(expression) ? expression.join() : expression;
    return x;
};
var DEFAULT_DASHBOARD_ITEMS = ['icicle'];
/* eslint-disable @typescript-eslint/naming-convention */
var sanitizeDateRange = function (time_selection_a, from_a, to_a) {
    var range = DateTimeRange.fromRangeKey(time_selection_a);
    if (from_a == null && to_a == null) {
        from_a = range.getFromMs();
        to_a = range.getToMs();
    }
    return { time_selection_a: range.getRangeKey(), from_a: from_a, to_a: to_a };
};
/* eslint-enable @typescript-eslint/naming-convention */
var filterSuffix = function (o, suffix) {
    return Object.fromEntries(Object.entries(o).filter(function (_a) {
        var key = _a[0];
        return !key.endsWith(suffix);
    }));
};
var swapQueryParameters = function (o) {
    Object.entries(o).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        if (key.endsWith('_b')) {
            o[key.slice(0, -2) + '_a'] = value;
        }
    });
    return o;
};
var ProfileExplorerApp = function (_a) {
    var _b, _c;
    var queryClient = _a.queryClient, queryParams = _a.queryParams, navigateTo = _a.navigateTo;
    var _d = useProfileTypes(queryClient), profileTypesLoading = _d.loading, profileTypesData = _d.data, profileTypesError = _d.error;
    var _e = useParcaContext(), loader = _e.loader, noDataPrompt = _e.noDataPrompt, onError = _e.onError;
    useEffect(function () {
        if (profileTypesError !== undefined && profileTypesError !== null) {
            onError === null || onError === void 0 ? void 0 : onError(profileTypesError, 'ProfileExplorer');
        }
    }, [profileTypesError, onError]);
    /* eslint-disable @typescript-eslint/naming-convention */
    var from_a = queryParams.from_a, to_a = queryParams.to_a, profile_name_a = queryParams.profile_name_a, labels_a = queryParams.labels_a, merge_from_a = queryParams.merge_from_a, merge_to_a = queryParams.merge_to_a, time_selection_a = queryParams.time_selection_a, compare_a = queryParams.compare_a, from_b = queryParams.from_b, to_b = queryParams.to_b, profile_name_b = queryParams.profile_name_b, labels_b = queryParams.labels_b, merge_from_b = queryParams.merge_from_b, merge_to_b = queryParams.merge_to_b, time_selection_b = queryParams.time_selection_b, compare_b = queryParams.compare_b, filter_by_function = queryParams.filter_by_function, dashboard_items = queryParams.dashboard_items;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    var expression_a = getExpressionAsAString(queryParams.expression_a);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    var expression_b = getExpressionAsAString(queryParams.expression_b);
    /* eslint-enable @typescript-eslint/naming-convention */
    var _f = useState(null), profileA = _f[0], setProfileA = _f[1];
    var _g = useState(null), profileB = _g[0], setProfileB = _g[1];
    useEffect(function () {
        var _a;
        var mergeFrom = merge_from_a !== null && merge_from_a !== void 0 ? merge_from_a : undefined;
        var mergeTo = merge_to_a !== null && merge_to_a !== void 0 ? merge_to_a : undefined;
        var labels = typeof labels_a === 'string' ? [labels_a] : (_a = labels_a) !== null && _a !== void 0 ? _a : [''];
        var profileA = ProfileSelectionFromParams(expression_a, from_a, to_a, mergeFrom, mergeTo, labels, filter_by_function);
        setProfileA(profileA);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merge_from_a, merge_to_a, filter_by_function]);
    useEffect(function () {
        var _a;
        var mergeFrom = merge_from_b !== null && merge_from_b !== void 0 ? merge_from_b : undefined;
        var mergeTo = merge_to_b !== null && merge_to_b !== void 0 ? merge_to_b : undefined;
        var labels = typeof labels_b === 'string' ? [labels_b] : (_a = labels_b) !== null && _a !== void 0 ? _a : [''];
        var profileB = ProfileSelectionFromParams(expression_b, from_b, to_b, mergeFrom, mergeTo, labels, filter_by_function);
        setProfileB(profileB);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merge_from_b, merge_to_b, filter_by_function]);
    if (profileTypesLoading) {
        return _jsx(_Fragment, { children: loader });
    }
    if ((profileTypesData === null || profileTypesData === void 0 ? void 0 : profileTypesData.types.length) === 0) {
        return _jsx(_Fragment, { children: noDataPrompt });
    }
    if (profileTypesError !== undefined && profileTypesError !== null) {
        return (_jsxs("div", __assign({ className: "relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700", role: "alert" }, { children: [_jsx("strong", __assign({ className: "font-bold" }, { children: "Error! " })), _jsx("span", __assign({ className: "block sm:inline" }, { children: profileTypesError.message }))] })));
    }
    var sanitizedRange = sanitizeDateRange(time_selection_a, from_a, to_a);
    time_selection_a = sanitizedRange.time_selection_a;
    from_a = sanitizedRange.from_a;
    to_a = sanitizedRange.to_a;
    if (((_b = queryParams === null || queryParams === void 0 ? void 0 : queryParams.expression_a) !== null && _b !== void 0 ? _b : '') !== '')
        queryParams.expression_a = expression_a;
    if (((_c = queryParams === null || queryParams === void 0 ? void 0 : queryParams.expression_b) !== null && _c !== void 0 ? _c : '') !== '')
        queryParams.expression_b = expression_b;
    var selectProfile = function (p, suffix) {
        queryParams.expression_a = encodeURIComponent(queryParams.expression_a);
        queryParams.expression_b = encodeURIComponent(queryParams.expression_b);
        return navigateTo('/', __assign(__assign(__assign({}, queryParams), SuffixParams(p.HistoryParams(), suffix)), { dashboard_items: dashboard_items !== null && dashboard_items !== void 0 ? dashboard_items : DEFAULT_DASHBOARD_ITEMS }));
    };
    var selectProfileA = function (p) {
        return selectProfile(p, '_a');
    };
    var selectProfileB = function (p) {
        return selectProfile(p, '_b');
    };
    var queryA = {
        expression: expression_a,
        from: parseInt(from_a),
        to: parseInt(to_a),
        timeSelection: time_selection_a,
        profile_name: profile_name_a,
        filter_by_function: filter_by_function,
    };
    // Show the SingleProfileExplorer when not comparing
    if (compare_a !== 'true' && compare_b !== 'true') {
        var selectQuery = function (q) {
            var mergeParams = q.mergeFrom !== undefined && q.mergeTo !== undefined
                ? { merge_from_a: q.mergeFrom, merge_to_a: q.mergeTo }
                : {};
            return navigateTo('/', __assign(__assign({}, filterSuffix(queryParams, '_a')), __assign({ expression_a: encodeURIComponent(q.expression), from_a: q.from.toString(), to_a: q.to.toString(), time_selection_a: q.timeSelection, filter_by_function: q.filter_by_function, dashboard_items: dashboard_items !== null && dashboard_items !== void 0 ? dashboard_items : DEFAULT_DASHBOARD_ITEMS }, mergeParams)));
        };
        var selectProfile_1 = function (p) {
            queryParams.expression_a = encodeURIComponent(queryParams.expression_a);
            return navigateTo('/', __assign(__assign(__assign({}, queryParams), SuffixParams(p.HistoryParams(), '_a')), { dashboard_items: dashboard_items !== null && dashboard_items !== void 0 ? dashboard_items : DEFAULT_DASHBOARD_ITEMS }));
        };
        var compareProfile = function () {
            var compareQuery = {
                compare_a: 'true',
                expression_a: encodeURIComponent(queryA.expression),
                from_a: queryA.from.toString(),
                to_a: queryA.to.toString(),
                time_selection_a: queryA.timeSelection,
                profile_name_a: queryA.profile_name,
                compare_b: 'true',
                expression_b: encodeURIComponent(queryA.expression),
                from_b: queryA.from.toString(),
                to_b: queryA.to.toString(),
                time_selection_b: queryA.timeSelection,
                profile_name_b: queryA.profile_name,
            };
            if (profileA != null) {
                compareQuery = __assign(__assign({}, SuffixParams(profileA.HistoryParams(), '_a')), compareQuery);
            }
            void navigateTo('/', __assign(__assign({}, compareQuery), { search_string: '', dashboard_items: dashboard_items !== null && dashboard_items !== void 0 ? dashboard_items : DEFAULT_DASHBOARD_ITEMS }));
        };
        return (_jsx(ProfileExplorerSingle, { queryClient: queryClient, query: queryA, profile: profileA, selectQuery: selectQuery, selectProfile: selectProfile_1, compareProfile: compareProfile, navigateTo: navigateTo }));
    }
    var queryB = {
        expression: expression_b,
        from: parseInt(from_b),
        to: parseInt(to_b),
        timeSelection: time_selection_b,
        profile_name: profile_name_b,
    };
    var selectQueryA = function (q) {
        var mergeParams = q.mergeFrom !== undefined && q.mergeTo !== undefined
            ? { merge_from_a: q.mergeFrom, merge_to_a: q.mergeTo }
            : {};
        return navigateTo('/', __assign(__assign({}, filterSuffix(queryParams, '_a')), __assign({ compare_a: 'true', expression_a: encodeURIComponent(q.expression), expression_b: encodeURIComponent(expression_b), from_a: q.from.toString(), to_a: q.to.toString(), time_selection_a: q.timeSelection, filter_by_function: filter_by_function !== null && filter_by_function !== void 0 ? filter_by_function : '', dashboard_items: dashboard_items !== null && dashboard_items !== void 0 ? dashboard_items : DEFAULT_DASHBOARD_ITEMS }, mergeParams)));
    };
    var selectQueryB = function (q) {
        var mergeParams = q.mergeFrom !== undefined && q.mergeTo !== undefined
            ? { merge_from_b: q.mergeFrom, merge_to_b: q.mergeTo }
            : {};
        return navigateTo('/', __assign(__assign({}, filterSuffix(queryParams, '_b')), __assign({ compare_b: 'true', expression_b: encodeURIComponent(q.expression), expression_a: encodeURIComponent(expression_a), from_b: q.from.toString(), to_b: q.to.toString(), time_selection_b: q.timeSelection, filter_by_function: filter_by_function !== null && filter_by_function !== void 0 ? filter_by_function : '', dashboard_items: dashboard_items !== null && dashboard_items !== void 0 ? dashboard_items : DEFAULT_DASHBOARD_ITEMS }, mergeParams)));
    };
    var closeProfile = function (card) {
        var newQueryParameters = queryParams;
        if (card === 'A') {
            newQueryParameters = swapQueryParameters(queryParams);
        }
        return navigateTo('/', __assign(__assign({}, filterSuffix(newQueryParameters, '_b')), {
            compare_a: 'false',
            compare_b: 'false',
            search_string: '',
        }));
    };
    return (_jsx(ProfileExplorerCompare, { queryClient: queryClient, queryA: queryA, queryB: queryB, profileA: profileA, profileB: profileB, selectQueryA: selectQueryA, selectQueryB: selectQueryB, selectProfileA: selectProfileA, selectProfileB: selectProfileB, closeProfile: closeProfile, navigateTo: navigateTo }));
};
var ProfileExplorer = function (_a) {
    var queryClient = _a.queryClient, queryParams = _a.queryParams, navigateTo = _a.navigateTo;
    var reduxStore = store().store;
    return (_jsx(Provider, __assign({ store: reduxStore }, { children: _jsx(KeyDownProvider, { children: _jsx(ProfileExplorerApp, { queryClient: queryClient, queryParams: queryParams, navigateTo: navigateTo }) }) })));
};
export default ProfileExplorer;
